import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, ChevronDown, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, ExternalLink, Image as ImageIcon, Upload } from "lucide-react";
import ImageField from "@/components/admin/ImageField";

/* ---------------- Hero Slide Manager (inline) ---------------- */
function HeroSlideManager({ onUpdated }) {
  const [slides, setSlides] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get("/admin/hero-slides"); setSlides(r.data); }
    catch { toast.error("Could not load hero slides"); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const form = new FormData(); form.append("file", f);
        const up = await api.post("/admin/upload", form);
        const url = up.data.urls?.large || up.data.url;
        await api.post("/admin/hero-slides", { image_url: url, alt: f.name.replace(/\.[^.]+$/, "") });
        ok++;
      } catch { /* swallow individual failures */ }
    }
    setUploading(false);
    if (ok) toast.success(`Added ${ok} slide${ok > 1 ? "s" : ""}`);
    load(); onUpdated?.();
  };

  const toggle = async (s) => {
    await api.put(`/admin/hero-slides/${s.id}`, { enabled: !s.enabled });
    load(); onUpdated?.();
  };

  const remove = async (s) => {
    if (!window.confirm("Delete this slide?")) return;
    await api.delete(`/admin/hero-slides/${s.id}`);
    toast.success("Deleted");
    load(); onUpdated?.();
  };

  const move = async (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= slides.length) return;
    const reordered = [...slides];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setSlides(reordered);
    await api.post("/admin/hero-slides/reorder", { ordered_ids: reordered.map(s => s.id) });
    onUpdated?.();
  };

  const updateField = async (s, patch) => {
    await api.put(`/admin/hero-slides/${s.id}`, patch);
    load(); onUpdated?.();
  };

  return (
    <div className="space-y-4" data-testid="hero-slide-manager">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-white/85 font-medium">Hero Slides</div>
          <div className="text-xs text-white/40 mt-0.5">{slides.length} slide{slides.length === 1 ? "" : "s"} · new uploads auto-appear at the end</div>
        </div>
        <input type="file" multiple accept="image/*" ref={fileRef} className="hidden"
          onChange={e => upload(Array.from(e.target.files || []))} data-testid="hero-slides-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold" data-testid="add-hero-slides-btn">
          <Upload size={14}/> {uploading ? "Uploading..." : "Upload Images"}
        </button>
      </div>

      {slides.length === 0 ? (
        <div className="border-2 border-dashed border-white/15 p-10 text-center text-white/40">
          <ImageIcon size={36} className="mx-auto mb-3 opacity-40" />
          No slides yet. Upload one or more images to start the slideshow.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {slides.map((s, idx) => (
            <div key={s.id} className={`relative bg-[#0a0a0a] border ${s.enabled ? "border-white/10" : "border-white/5 opacity-50"}`}
                 data-testid={`hero-slide-card-${s.id}`}>
              <div className="aspect-video overflow-hidden bg-black">
                <img src={s.image_url} alt={s.alt || ""} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                <input value={s.alt || ""} onBlur={e => updateField(s, { alt: e.target.value })}
                  defaultValue={s.alt || ""}
                  placeholder="Alt text (SEO)"
                  className="w-full bg-black border border-white/15 px-2 py-1.5 text-xs focus:border-[#D4AF37] outline-none"
                  data-testid={`slide-alt-${s.id}`} />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up"
                      className="p-1.5 text-white/60 hover:text-[#D4AF37] disabled:opacity-30">
                      <ArrowUp size={12}/>
                    </button>
                    <button onClick={() => move(idx, 1)} disabled={idx === slides.length - 1} title="Move down"
                      className="p-1.5 text-white/60 hover:text-[#D4AF37] disabled:opacity-30">
                      <ArrowDown size={12}/>
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggle(s)} title={s.enabled ? "Hide" : "Show"}
                      className={`p-1.5 ${s.enabled ? "text-[#D4AF37]" : "text-white/40"} hover:bg-white/5`}>
                      {s.enabled ? <Eye size={12}/> : <EyeOff size={12}/>}
                    </button>
                    <button onClick={() => remove(s)} title="Delete"
                      className="p-1.5 text-white/40 hover:text-red-500">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
                <div className="text-[0.55rem] tracking-widest uppercase text-white/30">Order #{idx + 1}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Reusable section panel ---------------- */
function SectionPanel({ id, title, hint, defaultOpen = false, children, onReset }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen(o => !o);
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } };
  return (
    <div className="bg-[#0a0a0a] border border-white/10" data-testid={`section-${id}`}>
      <div role="button" tabIndex={0} onClick={toggle} onKeyDown={onKey}
        className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02] transition cursor-pointer select-none">
        <div className="flex items-center gap-3 text-left">
          {open ? <ChevronDown size={16} className="text-[#D4AF37]"/> : <ChevronRight size={16} className="text-white/60"/>}
          <div>
            <div className="font-serif text-lg">{title}</div>
            {hint && <div className="text-xs text-white/40 mt-0.5">{hint}</div>}
          </div>
        </div>
        {onReset && open && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); if (window.confirm(`Reset "${title}" to defaults?`)) onReset(); }}
            className="text-[0.6rem] tracking-widest uppercase text-white/40 hover:text-[#D4AF37] flex items-center gap-1.5">
            <RotateCcw size={11}/> Reset
          </button>
        )}
      </div>
      {open && <div className="p-5 pt-0 space-y-4 border-t border-white/5">{children}</div>}
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">{label}</div>
    {hint && <div className="text-xs text-white/35 mt-0.5">{hint}</div>}
    <div className="mt-1">{children}</div>
  </label>
);

const TextInput = ({ value, onChange, placeholder, testid }) => (
  <input type="text" value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
);
const TextArea = ({ value, onChange, rows = 3, placeholder, testid }) => (
  <textarea value={value ?? ""} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testid}
    className="w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
);

/* ---------------- Main editor ---------------- */
export default function HomepageEditor() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("ep_token")) { nav("/admin/login"); return; }
    api.get("/admin/homepage").then(r => setData(r.data)).catch(() => toast.error("Could not load homepage content"));
  }, [nav]);

  const upd = (path, value) => {
    setData(d => {
      const next = JSON.parse(JSON.stringify(d || {}));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] || {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const updBenefit = (section, idx, key, value) => {
    setData(d => {
      const next = JSON.parse(JSON.stringify(d));
      const arr = (next[section].benefits || []).slice();
      arr[idx] = { ...arr[idx], [key]: value };
      next[section].benefits = arr;
      return next;
    });
    setDirty(true);
  };
  const addBenefit = (section) => {
    setData(d => {
      const next = JSON.parse(JSON.stringify(d));
      next[section].benefits = [...(next[section].benefits || []), { icon: "Sparkles", text: "New benefit" }];
      return next;
    });
    setDirty(true);
  };
  const removeBenefit = (section, idx) => {
    setData(d => {
      const next = JSON.parse(JSON.stringify(d));
      next[section].benefits = (next[section].benefits || []).filter((_, i) => i !== idx);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload._id; delete payload.id; delete payload.hero_slides; delete payload.updated_at;
      const r = await api.put("/admin/homepage", payload);
      setData(r.data);
      setDirty(false);
      toast.success("Homepage saved — changes are live");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const reset = async (section) => {
    try {
      const r = await api.post(`/admin/homepage/reset/${section}`);
      setData(r.data);
      setDirty(false);
      toast.success(`"${section}" reset to defaults`);
    } catch { toast.error("Reset failed"); }
  };

  if (!data) return <div className="text-white/40 py-10">Loading homepage content...</div>;

  return (
    <div className="space-y-4" data-testid="homepage-editor">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-10 bg-[#070707] -mx-8 px-8 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="text-xs text-white/50">
          Editing homepage content · {dirty ? <span className="text-[#D4AF37]">Unsaved changes</span> : <span className="text-green-400">All saved</span>}
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="btn-ghost text-xs"><ExternalLink size={12}/> Preview Live Site</a>
          <button onClick={save} disabled={saving || !dirty} className="btn-gold disabled:opacity-40" data-testid="homepage-save-btn">
            <Save size={14}/> {saving ? "Saving..." : "Save All Changes"}
          </button>
        </div>
      </div>

      {/* HERO */}
      <SectionPanel id="hero" title="Hero (Top of Homepage)" hint="Headline, subheadline, CTAs, slideshow & video toggle"
        defaultOpen onReset={() => reset("hero")}>
        <Field label="Eyebrow Tagline">
          <TextInput value={data.hero?.eyebrow} onChange={v => upd("hero.eyebrow", v)} testid="hero-eyebrow"/>
        </Field>
        <Field label="Main Headline (HTML allowed — wrap accent words in <em>...</em>)">
          <TextArea value={data.hero?.headline_html} onChange={v => upd("hero.headline_html", v)} rows={2} testid="hero-headline"/>
        </Field>
        <Field label="Subheadline">
          <TextArea value={data.hero?.subheadline} onChange={v => upd("hero.subheadline", v)} rows={2} testid="hero-subheadline"/>
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Primary Button — Label"><TextInput value={data.hero?.primary_cta_label} onChange={v => upd("hero.primary_cta_label", v)}/></Field>
          <Field label="Primary Button — Link"><TextInput value={data.hero?.primary_cta_link} onChange={v => upd("hero.primary_cta_link", v)} placeholder="/portfolio"/></Field>
          <Field label="Secondary Button — Label"><TextInput value={data.hero?.secondary_cta_label} onChange={v => upd("hero.secondary_cta_label", v)}/></Field>
          <Field label="Secondary Button — Link"><TextInput value={data.hero?.secondary_cta_link} onChange={v => upd("hero.secondary_cta_link", v)}/></Field>
          <Field label="Third Button — Label"><TextInput value={data.hero?.tertiary_cta_label} onChange={v => upd("hero.tertiary_cta_label", v)}/></Field>
          <Field label="Third Button — Link"><TextInput value={data.hero?.tertiary_cta_link} onChange={v => upd("hero.tertiary_cta_link", v)}/></Field>
        </div>

        <div className="border-t border-white/5 pt-4 mt-2">
          <div className="text-sm text-white/85 font-medium mb-2">Background mode</div>
          <div className="flex gap-2 mb-4">
            {[{ k: "slideshow", label: "Image Slideshow" }, { k: "video", label: "Background Video" }].map(opt => (
              <button key={opt.k} type="button" onClick={() => upd("hero.mode", opt.k)}
                data-testid={`hero-mode-${opt.k}`}
                className={`px-4 py-2 text-xs tracking-wide uppercase border ${data.hero?.mode === opt.k ? "border-[#D4AF37] bg-[#D4AF37] text-black" : "border-white/15 text-white/60 hover:text-white"}`}>
                {opt.label}
              </button>
            ))}
          </div>

          {data.hero?.mode === "slideshow" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <Field label="Slideshow Enabled">
                  <label className="flex items-center gap-2 mt-1">
                    <input type="checkbox" checked={data.hero?.slideshow_enabled !== false}
                      onChange={e => upd("hero.slideshow_enabled", e.target.checked)} className="accent-[#D4AF37]"
                      data-testid="hero-slideshow-enabled" />
                    <span className="text-sm text-white/80">Auto-rotate hero images</span>
                  </label>
                </Field>
                <Field label={`Rotation speed: ${(data.hero?.slideshow_interval_ms || 5000)/1000}s per slide`}>
                  <input type="range" min={2000} max={15000} step={500}
                    value={data.hero?.slideshow_interval_ms || 5000}
                    onChange={e => upd("hero.slideshow_interval_ms", parseInt(e.target.value))}
                    data-testid="hero-slideshow-speed"
                    className="w-full accent-[#D4AF37]" />
                </Field>
              </div>
              <HeroSlideManager />
            </>
          )}
        </div>
      </SectionPanel>

      {/* WHO WE ARE */}
      <SectionPanel id="who-we-are" title="Who We Are" onReset={() => reset("who_we_are")}>
        <Field label="Eyebrow"><TextInput value={data.who_we_are?.eyebrow} onChange={v => upd("who_we_are.eyebrow", v)}/></Field>
        <Field label="Headline (HTML allowed)"><TextArea rows={2} value={data.who_we_are?.headline_html} onChange={v => upd("who_we_are.headline_html", v)}/></Field>
        <Field label="Body Paragraph 1"><TextArea value={data.who_we_are?.body_1} onChange={v => upd("who_we_are.body_1", v)}/></Field>
        <Field label="Body Paragraph 2"><TextArea value={data.who_we_are?.body_2} onChange={v => upd("who_we_are.body_2", v)}/></Field>
        <Field label="Section Image">
          <ImageField value={data.who_we_are?.image_url} onChange={v => upd("who_we_are.image_url", v)} aspect="aspect-[4/5] max-w-sm" testId="who-image"/>
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Stat — Number"><TextInput value={data.who_we_are?.stat_number} onChange={v => upd("who_we_are.stat_number", v)}/></Field>
          <Field label="Stat — Label"><TextInput value={data.who_we_are?.stat_label} onChange={v => upd("who_we_are.stat_label", v)}/></Field>
        </div>
        <Field label="Bullet points (one per line)">
          <TextArea rows={6}
            value={(data.who_we_are?.bullets || []).join("\n")}
            onChange={v => upd("who_we_are.bullets", v.split("\n").map(s => s.trim()).filter(Boolean))} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Button — Label"><TextInput value={data.who_we_are?.cta_label} onChange={v => upd("who_we_are.cta_label", v)}/></Field>
          <Field label="Button — Link"><TextInput value={data.who_we_are?.cta_link} onChange={v => upd("who_we_are.cta_link", v)}/></Field>
        </div>
      </SectionPanel>

      {/* SIMPLE INTRO SECTIONS */}
      {[
        ["why_choose_us", "Why Choose Us"],
        ["featured_portfolio", "Featured Portfolio — Section Intro"],
        ["featured_films", "Featured Wedding Films — Section Intro"],
        ["process", "Process — Section Intro"],
        ["testimonials_section", "Testimonials — Section Intro"],
        ["google_reviews_section", "Google Reviews — Section Intro"],
        ["availability_section", "Availability Form — Section Intro"],
        ["packages_section", "Packages — Section Intro"],
        ["faq_section", "FAQ — Section Intro"],
      ].map(([key, label]) => (
        <SectionPanel key={key} id={key} title={label} onReset={() => reset(key)}>
          <Field label="Eyebrow"><TextInput value={data[key]?.eyebrow} onChange={v => upd(`${key}.eyebrow`, v)}/></Field>
          <Field label="Headline (HTML allowed)"><TextArea rows={2} value={data[key]?.headline_html} onChange={v => upd(`${key}.headline_html`, v)}/></Field>
          <Field label="Body (intro text)"><TextArea value={data[key]?.body} onChange={v => upd(`${key}.body`, v)}/></Field>
        </SectionPanel>
      ))}

      {/* SNAPPIC & STORY SONG — full editors */}
      {[
        { key: "snappic_section", label: "SnapPic Instant Photo Experience", testid: "snappic" },
        { key: "story_song_section", label: "Your Story Song Experience", testid: "story-song" },
      ].map(({ key, label, testid }) => (
        <SectionPanel key={key} id={testid} title={label} hint="Signature service section — full editor"
          onReset={() => reset(key)}>
          <Field label="Visible on homepage">
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={data[key]?.enabled !== false}
                onChange={e => upd(`${key}.enabled`, e.target.checked)} className="accent-[#D4AF37]"
                data-testid={`${testid}-enabled`} />
              <span className="text-sm text-white/80">Show this section</span>
            </label>
          </Field>
          <Field label="Eyebrow"><TextInput value={data[key]?.eyebrow} onChange={v => upd(`${key}.eyebrow`, v)}/></Field>
          <Field label="Section Title"><TextInput value={data[key]?.title} onChange={v => upd(`${key}.title`, v)}/></Field>
          <Field label="Subtitle"><TextInput value={data[key]?.subtitle} onChange={v => upd(`${key}.subtitle`, v)}/></Field>
          <Field label="Description"><TextArea rows={4} value={data[key]?.description} onChange={v => upd(`${key}.description`, v)}/></Field>
          <Field label="Marketing Highlight (quote)"><TextArea rows={2} value={data[key]?.marketing_highlight} onChange={v => upd(`${key}.marketing_highlight`, v)}/></Field>
          <Field label="Section Image">
            <ImageField value={data[key]?.image_url} onChange={v => upd(`${key}.image_url`, v)} aspect="aspect-video" testId={`${testid}-image`}/>
          </Field>
          <Field label="Benefits (with icons)">
            <div className="space-y-2">
              {(data[key]?.benefits || []).map((b, i) => (
                <div key={i} className="flex gap-2 items-center bg-black border border-white/10 p-2">
                  <TextInput value={b.icon} onChange={v => updBenefit(key, i, "icon", v)} placeholder="Lucide icon name (e.g. Sparkles)"/>
                  <TextInput value={b.text} onChange={v => updBenefit(key, i, "text", v)} placeholder="Benefit text"/>
                  <button onClick={() => removeBenefit(key, i)} className="text-white/40 hover:text-red-500 p-2 shrink-0"><Trash2 size={14}/></button>
                </div>
              ))}
              <button onClick={() => addBenefit(key)} className="btn-ghost text-xs"><Plus size={12}/> Add benefit</button>
            </div>
            <div className="text-[0.55rem] text-white/30 mt-2">Icons use Lucide names — e.g. <code>Zap, QrCode, Smartphone, Share2, Sparkles, Music, Disc, Film, Heart, Infinity, Star, Camera</code></div>
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Button — Label"><TextInput value={data[key]?.cta_label} onChange={v => upd(`${key}.cta_label`, v)}/></Field>
            <Field label="Button — Link"><TextInput value={data[key]?.cta_link} onChange={v => upd(`${key}.cta_link`, v)}/></Field>
          </div>
        </SectionPanel>
      ))}

      {/* CTA STRIP */}
      <SectionPanel id="cta-strip" title="Bottom CTA Strip" onReset={() => reset("cta_strip")}>
        <Field label="Eyebrow"><TextInput value={data.cta_strip?.eyebrow} onChange={v => upd("cta_strip.eyebrow", v)}/></Field>
        <Field label="Headline (HTML allowed)"><TextArea rows={2} value={data.cta_strip?.headline_html} onChange={v => upd("cta_strip.headline_html", v)}/></Field>
        <Field label="Body"><TextArea value={data.cta_strip?.body} onChange={v => upd("cta_strip.body", v)}/></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Primary Button Label"><TextInput value={data.cta_strip?.primary_cta_label} onChange={v => upd("cta_strip.primary_cta_label", v)}/></Field>
          <Field label="Primary Button Link"><TextInput value={data.cta_strip?.primary_cta_link} onChange={v => upd("cta_strip.primary_cta_link", v)}/></Field>
          <Field label="Secondary Button Label"><TextInput value={data.cta_strip?.secondary_cta_label} onChange={v => upd("cta_strip.secondary_cta_label", v)}/></Field>
          <Field label="Secondary Button Link"><TextInput value={data.cta_strip?.secondary_cta_link} onChange={v => upd("cta_strip.secondary_cta_link", v)}/></Field>
        </div>
      </SectionPanel>

      {/* SEO */}
      <SectionPanel id="seo" title="Homepage SEO" hint="Title, meta description and Open Graph image" onReset={() => reset("seo")}>
        <Field label="Page Title (under 60 chars)">
          <TextInput value={data.seo?.title} onChange={v => upd("seo.title", v)} testid="seo-title"/>
          <span className="text-[0.55rem] text-white/30">{(data.seo?.title || "").length}/60</span>
        </Field>
        <Field label="Meta Description (under 160 chars)">
          <TextArea value={data.seo?.description} onChange={v => upd("seo.description", v)} testid="seo-description"/>
          <span className="text-[0.55rem] text-white/30">{(data.seo?.description || "").length}/160</span>
        </Field>
        <Field label="Open Graph image (shown when shared on social)">
          <ImageField value={data.seo?.og_image} onChange={v => upd("seo.og_image", v)} aspect="aspect-video" testId="seo-og"/>
        </Field>
      </SectionPanel>
    </div>
  );
}
